import { type GeocodeResult, slugToQuery } from '@mollusk/shared';
import { stripStreetNumber } from '@mollusk/shared';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { z } from 'zod';
import { ApiError } from '../api/client';
import { api } from '../api/endpoints';
import { LoadingPill } from '../components/LoadingPill';
import { useIsAdmin } from '../features/auth/internal';
import {
  AddressDrilldownModal,
  type DrilldownScope,
} from '../features/counts/AddressDrilldownModal';
import { CountsBox } from '../features/counts/CountsBox';
import { ParcelMap } from '../features/parcels/ParcelMap';
import { pickTargetParcel } from '../features/parcels/pickTargetParcel';
import { PermitsResult } from '../features/permits/PermitsResult';
import { addressTagCounts } from '../features/permits/permitView';
import { useMapboxSessionStore } from '../stores/mapboxSession';

function countLabels(t: GeocodeResult) {
  const street = t.street_name ?? (t.line1 ? stripStreetNumber(t.line1) : '');
  return {
    address: t.formatted_address,
    street: [street, t.city, t.state].filter(Boolean).join(', '),
    zip: t.postal_code ? `ZIP ${t.postal_code}` : 'ZIP',
    city: [t.city, t.state].filter(Boolean).join(', '),
  };
}

function drilldownFor(scope: 'street' | 'zip' | 'city', t: GeocodeResult): DrilldownScope | null {
  const street = t.street_name ?? (t.line1 ? stripStreetNumber(t.line1) : null);
  if (scope === 'street' && street && t.city && t.state && t.postal_code) {
    return { scope: 'street', street, city: t.city, state: t.state, postal_code: t.postal_code };
  }
  if (scope === 'city' && t.city && t.state) return { scope: 'city', city: t.city, state: t.state };
  if (scope === 'zip' && t.postal_code) return { scope: 'zip', postal_code: t.postal_code };
  return null;
}

function AddressDetailPage() {
  const isAdmin = useIsAdmin();
  const [drilldown, setDrilldown] = useState<DrilldownScope | null>(null);
  const { slug } = Route.useParams();
  const { mid } = Route.useSearch();
  const sessionToken = useMapboxSessionStore((s) => s.sessionToken);

  const geocode = useQuery({
    queryKey: ['geocode', slug, mid ?? null],
    queryFn: () =>
      mid ? api.geocodeById(mid, sessionToken) : api.geocodeByQuery(slugToQuery(slug)),
  });

  const target = geocode.data;

  const targetParcel = useQuery({
    queryKey: ['parcel-at', target?.latitude, target?.longitude, target?.line1],
    enabled: !!target,
    queryFn: async () => {
      if (!target) return null;
      // Tiny bbox around the geocoded point. The bbox can still catch
      // neighboring parcels (e.g. 4805 Ave G vs 4810 Ave H), so picking
      // the right one is delegated to pickTargetParcel — which prefers
      // an address-line1 match before falling back to point-in-polygon.
      const dx = 0.0008;
      const dy = 0.0008;
      const res = await api.parcels([
        target.longitude - dx,
        target.latitude - dy,
        target.longitude + dx,
        target.latitude + dy,
      ]);
      return pickTargetParcel(res.parcels, target);
    },
  });

  const permits = useQuery({
    queryKey: [
      'permits',
      target?.latitude,
      target?.longitude,
      target?.line1,
      targetParcel.data?.parcel_id_normalized,
    ],
    // Wait for the parcel lookup: its APN is part of the query, and starting
    // early showed "No permits found" before the real result (#171).
    enabled: !!target && !targetParcel.isPending,
    queryFn: () => {
      if (!target) throw new Error('no target');
      return api.permits({
        lat: target.latitude,
        lng: target.longitude,
        // Pass the address line AND its locality together: the line1 match is
        // pinned to city/state/ZIP so an identical street line in another city
        // doesn't leak into this address's permits (Austin vs Rosenberg
        // "4310 Avenue H").
        ...(target.line1
          ? {
              line1: target.line1,
              ...(target.city ? { city: target.city } : {}),
              ...(target.state ? { state: target.state } : {}),
              ...(target.postal_code ? { postal_code: target.postal_code } : {}),
            }
          : {}),
        ...(targetParcel.data?.parcel_id_normalized
          ? { apn: targetParcel.data.parcel_id_normalized }
          : {}),
      });
    },
  });

  const countsArgs = useMemo(() => {
    if (!target) return null;
    const street = target.street_name ?? (target.line1 ? stripStreetNumber(target.line1) : null);
    if (!street || !target.city || !target.state || !target.postal_code) return null;
    return {
      street,
      city: target.city,
      state: target.state,
      postal_code: target.postal_code,
    };
  }, [target]);

  const counts = useQuery({
    queryKey: ['counts', countsArgs],
    enabled: !!countsArgs,
    queryFn: () => {
      if (!countsArgs) throw new Error('no args');
      return api.counts(countsArgs);
    },
  });

  if (geocode.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (geocode.error) {
    const msg =
      geocode.error instanceof ApiError && geocode.error.code === 'GEOCODE_LOW_CONFIDENCE'
        ? 'Could not pin this address. Try refining.'
        : geocode.error instanceof Error
          ? geocode.error.message
          : 'Geocode failed.';
    return (
      <Alert severity="error" sx={{ maxWidth: 600, mx: 'auto', mt: 4 }}>
        {msg}
      </Alert>
    );
  }

  if (!target) return null;

  return (
    <Box>
      <Typography variant="h5" component="h1" sx={{ mb: 1 }}>
        {target.formatted_address}
      </Typography>
      {isAdmin ? (
        <Button
          href={`/planning-detail?q=${encodeURIComponent(targetParcel.data?.parcel_id_normalized ?? target.line1 ?? target.formatted_address)}${target.state ? `&state=${encodeURIComponent(target.state)}` : ''}`}
        >
          Planning cases at this location
        </Button>
      ) : null}

      <Box sx={{ mt: 3, mb: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Typography variant="h6">Permits at this address</Typography>
      </Box>
      <PermitsResult
        result={permits}
        loading={<LoadingPill data-testid="permits-loading" label="Loading permits…" />}
        failed="Permits could not be loaded. Try again in a moment."
        tooExpensive="This address would scan more data than allowed, so its permits can't be shown."
      />

      <Box
        sx={{
          mt: 3,
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: '1fr', md: '3fr 2fr' },
          alignItems: 'start',
        }}
      >
        <ParcelMap
          center={{ lat: target.latitude, lng: target.longitude }}
          targetParcel={targetParcel.data ?? null}
        />
        <CountsBox
          labels={countLabels(target)}
          address={permits.data ? addressTagCounts(permits.data.permits) : null}
          counts={counts.data}
          onShowAddresses={(scope) => setDrilldown(drilldownFor(scope, target))}
        />
      </Box>
      <AddressDrilldownModal
        open={!!drilldown}
        scope={drilldown}
        onClose={() => setDrilldown(null)}
      />
    </Box>
  );
}

export const Route = createFileRoute('/address/$slug')({
  component: AddressDetailPage,
  validateSearch: (search) => z.object({ mid: z.string().optional() }).parse(search),
});
